import {
    decryptAttachment,
    encryptAttachment,
} from 'matrix-encrypt-attachment';
import type { IEncryptedFile } from 'matrix-encrypt-attachment';

export interface MatrixEncryptedFile {
    url: string;
    key: IEncryptedFile['key'];
    iv: IEncryptedFile['iv'];
    hashes: NonNullable<IEncryptedFile['hashes']> & { sha256: string };
    v?: IEncryptedFile['v'];
}

const ALLOWED_BLOB_MIMETYPES = new Set([
    'image/jpeg',
    'image/gif',
    'image/png',
    'image/apng',
    'image/webp',
    'image/avif',
    'video/mp4',
    'video/webm',
    'video/ogg',
    'video/quicktime',
    'audio/mp4',
    'audio/webm',
    'audio/aac',
    'audio/mpeg',
    'audio/ogg',
    'audio/wave',
    'audio/wav',
    'audio/x-wav',
    'audio/x-pn-wav',
    'audio/flac',
    'audio/x-flac',
]);

export function getBlobSafeMimeType(mimetype?: string): string {
    const safeMimetype = mimetype?.split(';')[0].trim() ?? '';
    return ALLOWED_BLOB_MIMETYPES.has(safeMimetype) ? safeMimetype : 'application/octet-stream';
}

export function getEncryptedMediaCacheKey(file?: MatrixEncryptedFile, mimeType?: string): string {
    if (!file) return '';
    return [
        file.url,
        file.v ?? '',
        file.iv,
        file.hashes?.sha256 ?? '',
        mimeType ?? '',
    ].join('|');
}

export async function encryptMatrixAttachment(plaintext: ArrayBuffer): Promise<{
    data: ArrayBuffer;
    info: Omit<MatrixEncryptedFile, 'url'>;
}> {
    const result = await encryptAttachment(plaintext);
    return result as { data: ArrayBuffer; info: Omit<MatrixEncryptedFile, 'url'> };
}

function normalizeEncryptedFile(file: MatrixEncryptedFile): MatrixEncryptedFile {
    return {
        ...file,
        key: {
            ...file.key,
            k: file.key.k.replace(/[.=]+$/, ''),
        },
    };
}

export async function decryptMatrixAttachment(ciphertext: ArrayBuffer, file: MatrixEncryptedFile): Promise<ArrayBuffer> {
    return decryptAttachment(ciphertext, normalizeEncryptedFile(file));
}
